import React, { useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { getDir } from "orion-components/i18n/Config/selector";
import { Translate, getTranslation } from "orion-components/i18n";
import { Button } from "@mui/material";
import { timeConversion, restClient } from "client-app-core";
import { createUserFeedback } from "orion-components/Dock/Actions/index.js";

const getStyles = (dir) => ({
	container: {
		padding: "18px 14px"
	},
	title: {
		lineHeight: "16px",
		marginBottom: 16
	},
	actionRow: {
		display: "flex",
		alignItems: "center"
	},
	actionButton: {
		height: 26,
		color: "#fff",
		width: 185,
		padding: 0,
		fontSize: 12,
		fontWeight: "normal",
		...(dir === "rtl" ? { marginLeft: 28 } : { marginRight: 28 })
	},
	noButton: {
		color: "#BFBFBF",
		fontSize: 12,
		lineHeight: "12px",
		width: 185,
		...(dir === "rtl" ? { marginLeft: 28 } : { marginRight: 28 }),
		height: 26,
		display: "flex",
		flexDirection: "column",
		justifyContent: "center"
	},
	middleColumn: {
		fontSize: 10,
		flexGrow: 1,
		lineHeight: "10px"
	},
	rightColumn: {
		fontSize: 9,
		lineHeight: "10px"
	},
	divider: {
		backgroundColor: "#46484B",
		margin: "14px 0"
	}
});

const AlertAction = ({ action, alertTime, alertId, alertAcknowledged }) => {
	const dispatch = useDispatch();
	const dir = useSelector((state) => getDir(state));
	const [styles, setStyles] = useState({});
	const timeFormat = useSelector((state) => state.appState.global?.timeFormat);
	const locale = useSelector((state) => state.i18n.locale);
	const [timerText, setTimerText] = useState("");

	useEffect(() => {
		setStyles(getStyles(dir));
	}, [dir]);

	useEffect(() => {
		if (!action.result && (action.ackTimerEnabled || action.closeTimerEnabled)) {
			const endTime =
				new Date(alertTime).getTime() +
				durationToMilliseconds(action.actionTimerDuration, action.actionTimerUnit);

			const updateTimerText = () => {
				const now = new Date().getTime();
				const distance = endTime - now;

				const days = String(Math.floor(Math.abs(distance / (1000 * 60 * 60 * 24)))).padStart(2, "0");
				const hours = String(
					Math.floor(Math.abs((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)))
				).padStart(2, "0");
				const minutes = String(Math.floor(Math.abs((distance % (1000 * 60 * 60)) / (1000 * 60)))).padStart(
					2,
					"0"
				);
				const seconds = String(Math.floor(Math.abs((distance % (1000 * 60)) / 1000))).padStart(2, "0");
				const timerText = `${distance < 0 ? "+" : "-"}${
					days > 0 ? `${days}:` : ""
				}${hours}:${minutes}:${seconds}`;
				setTimerText(timerText);
			};

			updateTimerText();
			const interval = setInterval(updateTimerText, 1000);

			return () => clearInterval(interval);
		}
	}, [action, alertTime]);

	const durationToMilliseconds = (duration, units) => {
		let multiplier = 1000;
		switch (units) {
			case "sec":
				multiplier = 1000;
				break;
			case "min":
				multiplier = multiplier * 60;
				break;
			case "hr":
				multiplier = multiplier * 60 * 60;
				break;
			case "day":
				multiplier = multiplier * 60 * 60 * 24;
				break;
		}
		return duration * multiplier;
	};

	const actionButtonClicked = useCallback(
		(action) => {
			restClient.exec_post(
				"/ecosystem/api/alertActions/execute",
				JSON.stringify({
					alertActionId: action.alertActionId,
					alertActionInstanceId: action.alertActionInstanceId,
					alertId: alertId
				}),
				function (err, response) {
					if (err || response.success === false) {
						// Show error toast
						const errorToastMessage = getTranslation(
							"global.profiles.widgets.alarmActions.errorToast",
							action.label
						);
						dispatch(createUserFeedback(errorToastMessage, null, true));
						console.error(`An error occurred executing alert action: ${action.label}`, err, response);
					} else {
						// Show success toast
						const successToastMessage = getTranslation(
							"global.profiles.widgets.alarmActions.successToast",
							action.label
						);
						dispatch(createUserFeedback(successToastMessage, null, false));
					}
				}
			);
		},
		[alertId, dispatch]
	);

	const getLeftColumn = useCallback(
		(action) => {
			if (action.profileEnabled) {
				return (
					<Button
						key={`${action.alertActionInstanceId}-button`}
						variant="contained"
						onClick={() => {
							actionButtonClicked(action);
						}}
						color="primary"
						style={styles.actionButton}
						disabled={!!action.result}
						sx={{
							"&.Mui-disabled": {
								backgroundColor: "#46484B",
								color: "#9C9D9F !important"
							}
						}}
					>
						{action.label}
					</Button>
				);
			} else {
				return (
					<div style={styles.noButton}>
						<p>{action.label}</p>
					</div>
				);
			}
		},
		[actionButtonClicked, styles]
	);

	const getMiddleColumn = useCallback(
		(action) => {
			// COMPLETED, COUNTDOWN, FAILED TO SEND
			let i18nKey = "";
			let color = "";

			if (action.result?.success) {
				i18nKey = "completed";
				color = "#03C34C";
			} else if (action.result) {
				i18nKey = "failedToSend";
				color = "#E85758";
			} else if (action.ackTimerEnabled && alertAcknowledged) {
				i18nKey = "cancelled";
				color = "#C7C7C7";
			} else if (action.ackTimerEnabled || action.closeTimerEnabled) {
				i18nKey = "countdown";
				color = "#4BB2F1";
			}

			return (
				<div style={{ ...styles.middleColumn, color: color }}>
					<Translate value={`global.profiles.widgets.alarmActions.${i18nKey}`} />
				</div>
			);
		},
		[styles, alertAcknowledged]
	);

	const getRightColumn = useCallback(
		(action) => {
			if (action.result?.success) {
				const text = timeConversion.convertToUserTime(
					action.result.completedDate,
					`full_${timeFormat} `,
					locale
				);
				return (
					<div style={{ ...styles.rightColumn, color: "#C7C7C7" }}>
						<p>{text}</p>
					</div>
				);
			} else if (action.result) {
				return (
					<div
						style={{ ...styles.rightColumn, color: "#4BB2F1", padding: "8px 0", cursor: "pointer" }}
						onClick={() => actionButtonClicked(action)}
					>
						<Translate value="global.profiles.widgets.alarmActions.tryAgain" />
					</div>
				);
			} else if ((action.ackTimerEnabled && !alertAcknowledged) || action.closeTimerEnabled) {
				return (
					<div style={{ ...styles.rightColumn, color: "#C7C7C7" }}>
						<p>{timerText}</p>
					</div>
				);
			} else {
				return <div></div>;
			}
		},
		[actionButtonClicked, locale, styles, timeFormat, timerText, alertAcknowledged]
	);

	return (
		<>
			<div key={`${action.alertActionInstanceId}_row`} style={styles.actionRow}>
				{getLeftColumn(action)}
				{getMiddleColumn(action)}
				{getRightColumn(action)}
			</div>
		</>
	);
};

AlertAction.propTypes = {
	action: PropTypes.shape({
		alertActionId: PropTypes.string.isRequired,
		alertActionInstanceId: PropTypes.string.isRequired,
		label: PropTypes.string.isRequired,
		profileEnabled: PropTypes.bool.isRequired,
		ackTimerEnabled: PropTypes.bool,
		actionTimerDuration: PropTypes.number,
		actionTimerUnit: PropTypes.string,
		result: PropTypes.shape({
			success: PropTypes.bool,
			completedDate: PropTypes.string
		})
	}).isRequired,
	alertTime: PropTypes.string.isRequired,
	alertId: PropTypes.string.isRequired,
	alertAcknowledged: PropTypes.bool.isRequired
};

export default AlertAction;
